import React, { useState, useEffect } from 'react';
import apiService from '../../shared/services/ApiService';
import '../../css/roleForm.css';

// Agrupar permisos por modulo para la UI
const PERMISSION_GROUPS = {
    'Dashboard': ['dashboard:ver'],
    'Supervisor': ['supervisor:ver', 'supervisor:brigadas'],
    'Mapa': ['mapa:ver'],
    'Brigadas': ['brigadas:ver', 'brigadas:crear', 'brigadas:editar', 'brigadas:eliminar', 'brigadas:estado'],
    'Usuarios': ['usuarios:ver', 'usuarios:crear', 'usuarios:editar', 'usuarios:eliminar'],
    'Roles': ['roles:ver', 'roles:crear', 'roles:editar', 'roles:eliminar'],
    'Sectores': ['sectores:ver', 'sectores:crear', 'sectores:editar', 'sectores:eliminar', 'sectores:completar', 'sectores:admin_override'],
    'Circunscripciones': ['circunscripciones:ver', 'circunscripciones:crear', 'circunscripciones:editar', 'circunscripciones:eliminar'],
    'Reportes': ['reportes:ver', 'reportes:exportar'],
    'Reportes Ciudadanos': ['reportes_ciudadanos:ver', 'reportes_ciudadanos:gestionar', 'reportes_ciudadanos:asignar', 'reportes_ciudadanos:comentar', 'reportes_ciudadanos:estado', 'reportes_ciudadanos:eliminar', 'reportes_ciudadanos:exportar', 'reportes_ciudadanos:ver_asignados'],
};

const PERMISSION_LABELS = {
    'dashboard:ver': 'Ver dashboard',
    'supervisor:ver': 'Ver panel supervisor',
    'supervisor:brigadas': 'Ver brigadas en supervisor',
    'mapa:ver': 'Ver mapa',
    'brigadas:ver': 'Ver brigadas',
    'brigadas:crear': 'Crear brigadas',
    'brigadas:editar': 'Editar brigadas',
    'brigadas:eliminar': 'Eliminar brigadas',
    'brigadas:estado': 'Cambiar estado brigadas',
    'usuarios:ver': 'Ver usuarios',
    'usuarios:crear': 'Crear usuarios',
    'usuarios:editar': 'Editar usuarios',
    'usuarios:eliminar': 'Eliminar usuarios',
    'roles:ver': 'Ver roles',
    'roles:crear': 'Crear roles',
    'roles:editar': 'Editar roles',
    'roles:eliminar': 'Eliminar roles',
    'sectores:ver': 'Ver sectores',
    'sectores:crear': 'Crear sectores',
    'sectores:editar': 'Editar sectores',
    'sectores:eliminar': 'Eliminar sectores',
    'sectores:completar': 'Completar sectores',
    'sectores:admin_override': 'Override admin sectores',
    'circunscripciones:ver': 'Ver circunscripciones',
    'circunscripciones:crear': 'Crear circunscripciones',
    'circunscripciones:editar': 'Editar circunscripciones',
    'circunscripciones:eliminar': 'Eliminar circunscripciones',
    'reportes:ver': 'Ver reportes analiticos',
    'reportes:exportar': 'Exportar reportes',
    'reportes_ciudadanos:ver': 'Ver todos los reportes ciudadanos',
    'reportes_ciudadanos:gestionar': 'Gestionar reportes (admin general)',
    'reportes_ciudadanos:asignar': 'Asignar brigadas a reportes',
    'reportes_ciudadanos:comentar': 'Comentar en reportes',
    'reportes_ciudadanos:estado': 'Cambiar estado de reportes',
    'reportes_ciudadanos:eliminar': 'Eliminar reportes ciudadanos',
    'reportes_ciudadanos:exportar': 'Exportar reportes (PDF/Excel)',
    'reportes_ciudadanos:ver_asignados': 'Ver reportes asignados a mi brigada',
};

const RoleForm = ({ onSubmit, role, onCancel }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        permissions: []
    });
    const [errors, setErrors] = useState({});
    const [expandedGroups, setExpandedGroups] = useState({});

    useEffect(() => {
        if (role) {
            setFormData({
                name: role.name || '',
                description: role.description || '',
                permissions: role.permissions || []
            });
        } else {
            setFormData({
                name: '',
                description: '',
                permissions: []
            });
        }
    }, [role]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const togglePermission = (perm) => {
        setFormData(prev => {
            const exists = prev.permissions.includes(perm);
            return {
                ...prev,
                permissions: exists
                    ? prev.permissions.filter(p => p !== perm)
                    : [...prev.permissions, perm]
            };
        });
    };

    const toggleGroup = (groupName) => {
        const groupPerms = PERMISSION_GROUPS[groupName];
        const allSelected = groupPerms.every(p => formData.permissions.includes(p));
        setFormData(prev => ({
            ...prev,
            permissions: allSelected
                ? prev.permissions.filter(p => !groupPerms.includes(p))
                : [...new Set([...prev.permissions, ...groupPerms])]
        }));
    };

    const toggleExpandGroup = (groupName) => {
        setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
    };

    const selectAllPermissions = () => {
        const all = Object.values(PERMISSION_GROUPS).flat();
        setFormData(prev => ({ ...prev, permissions: all }));
    };

    const clearAllPermissions = () => {
        setFormData(prev => ({ ...prev, permissions: [] }));
    };

    const loadDefaults = async () => {
        try {
            const response = await apiService.get('/api/roles/permissions-list');
            const defaults = response.data.defaults;
            if (formData.name && defaults[formData.name]) {
                setFormData(prev => ({ ...prev, permissions: defaults[formData.name] }));
            }
        } catch (err) {
            console.error('Error cargando defaults:', err);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'El nombre del rol es requerido';
        } else if (formData.name.trim().length < 3) {
            newErrors.name = 'El nombre debe tener al menos 3 caracteres';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        onSubmit(formData);
    };

    return (
        <div className="role-form-container-modern">
            <div className="form-header-role">
                <div className="form-icon-role">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                </div>
                <h3 className="form-title-role">
                    {role ? 'Editar Rol' : 'Crear Nuevo Rol'}
                </h3>
                <p className="form-subtitle-role">
                    {role ? 'Modifica los detalles y permisos del rol' : 'Define un nuevo rol y sus permisos'}
                </p>
            </div>

            <form onSubmit={handleSubmit} className="role-form-modern">
                <div className="form-content-role">
                    {/* Nombre del rol */}
                    <div className={`form-group-role ${errors.name ? 'has-error' : ''}`}>
                        <label htmlFor="name" className="form-label-role">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                            </svg>
                            Nombre del Rol
                            <span className="required-role">*</span>
                        </label>
                        <input
                            id="name"
                            name="name"
                            type="text"
                            placeholder="Ej: Administrador, Operador, Supervisor..."
                            value={formData.name}
                            onChange={handleChange}
                            className={`form-input-role ${errors.name ? 'input-error' : ''}`}
                            maxLength={50}
                        />
                        {errors.name && (
                            <span className="error-message-role">
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="12" y1="8" x2="12" y2="12"/>
                                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                                {errors.name}
                            </span>
                        )}
                        <div className="input-hint-role">{formData.name.length}/50 caracteres</div>
                    </div>

                    {/* Descripcion */}
                    <div className="form-group-role">
                        <label htmlFor="description" className="form-label-role">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                            </svg>
                            Descripcion
                            <span className="optional-role">(Opcional)</span>
                        </label>
                        <textarea
                            id="description"
                            name="description"
                            placeholder="Describe las responsabilidades de este rol..."
                            value={formData.description}
                            onChange={handleChange}
                            className="form-textarea-role"
                            rows="3"
                            maxLength={500}
                        />
                        <div className="input-hint-role">{formData.description.length}/500 caracteres</div>
                    </div>

                    {/* Permisos */}
                    <div className="form-group-role">
                        <label className="form-label-role">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                            </svg>
                            Permisos ({formData.permissions.length} seleccionados)
                        </label>

                        <div className="permissions-actions">
                            <button type="button" className="perm-action-btn" onClick={selectAllPermissions}>Seleccionar todos</button>
                            <button type="button" className="perm-action-btn" onClick={clearAllPermissions}>Limpiar</button>
                            {formData.name && (
                                <button type="button" className="perm-action-btn perm-default" onClick={loadDefaults}>Cargar por defecto</button>
                            )}
                        </div>

                        <div className="permissions-container">
                            {Object.entries(PERMISSION_GROUPS).map(([groupName, perms]) => {
                                const selectedCount = perms.filter(p => formData.permissions.includes(p)).length;
                                const allSelected = selectedCount === perms.length;
                                const isExpanded = expandedGroups[groupName] !== false;

                                return (
                                    <div key={groupName} className="permission-group">
                                        <div className="permission-group-header" onClick={() => toggleExpandGroup(groupName)}>
                                            <div className="perm-group-left">
                                                <input
                                                    type="checkbox"
                                                    checked={allSelected}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        toggleGroup(groupName);
                                                    }}
                                                    className="perm-group-checkbox"
                                                />
                                                <span className="perm-group-name">{groupName}</span>
                                                <span className="perm-group-count">{selectedCount}/{perms.length}</span>
                                            </div>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                                fill="none" stroke="currentColor" strokeWidth="2"
                                                className={`perm-chevron ${isExpanded ? 'expanded' : ''}`}
                                            >
                                                <polyline points="6 9 12 15 18 9"/>
                                            </svg>
                                        </div>
                                        {isExpanded && (
                                            <div className="permission-group-items">
                                                {perms.map(perm => (
                                                    <label key={perm} className="permission-item">
                                                        <input
                                                            type="checkbox"
                                                            checked={formData.permissions.includes(perm)}
                                                            onChange={() => togglePermission(perm)}
                                                        />
                                                        <span className="perm-label">{PERMISSION_LABELS[perm] || perm}</span>
                                                        <code className="perm-code">{perm}</code>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="form-actions-role">
                    <button type="button" onClick={onCancel} className="cancel-button-role">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                        Cancelar
                    </button>
                    <button type="submit" className="submit-button-role">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        {role ? 'Actualizar Rol' : 'Crear Rol'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default RoleForm;
